import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../../../config/config";
import { getToken } from "../../../utils/localStorageMethods";
import i18n from "../i18n";
import { translateManyClient } from "../../../utils/ItClient";

const apiUrl = config.Url;
const norm = (l) => (l || "en").slice(0, 2);

// Create a baseQuery with auth headers
const baseQuery = fetchBaseQuery({
  baseUrl: `${apiUrl}/maintenance`,
  credentials: "include",
  prepareHeaders: (headers) => {
    const token = getToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("ngrok-skip-browser-warning", true);
    }
    return headers;
  },
});

export const maintainanceSlice = createApi({
  reducerPath: "maintainanceSlice",
  baseQuery: baseQuery,
  tagTypes: ["maintainanceSlice"],
  endpoints: (builder) => ({
    // GET all routesSchedules with filters

    getAllMaintainance: builder.query({
      query: ({ page, limit, search, maintenance_type }) => {
        const params = new URLSearchParams();

        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (search) params.append("search", search);
        if (maintenance_type)
          params.append("maintenance_type", maintenance_type);

        const queryString = params.toString();
        return queryString ? `?${queryString}` : "";
      },

      // Dynamic Data Localization
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18n.language || "en");
          if (lang === "en") return;

          const maintainance = data?.data?.maintenanceRecords ?? [];
          if (!Array.isArray(maintainance) || maintainance.length === 0) return;

          // Collect strings to translate (skip PII like names/emails)
          const bucket = [];
          for (const maintainanceRecord of maintainance) {
            if (maintainanceRecord.maintenance_type)
              bucket.push(maintainanceRecord.maintenance_type);
            if (maintainanceRecord.description)
              bucket.push(maintainanceRecord.description);
            if (maintainanceRecord.vehicle.vehicle_type)
              bucket.push(maintainanceRecord.vehicle.vehicle_type);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? map.get(s) ?? s : s);

          dispatch(
            maintainanceSlice.util.updateQueryData(
              "getAllMaintainance",
              arg,
              (draft) => {
                const arr = draft?.data?.maintenanceRecords ?? [];
                arr.forEach((maintainanceRecord) => {
                  maintainanceRecord.maintenance_type = pick(
                    maintainanceRecord.maintenance_type
                  );
                  maintainanceRecord.description = pick(
                    maintainanceRecord.description
                  );
                  maintainanceRecord.vehicle.vehicle_type = pick(
                    maintainanceRecord.vehicle.vehicle_type
                  );
                });
              }
            )
          );
        } catch {
          /* ignore translation errors */
        }
      },

      providesTags: ["maintainanceSlice"],
    }),

    // GET details of a single vehicle by ID
    getMaintainanceDetails: builder.query({
      query: (id) => `${id}`,
      providesTags: ["maintainanceSlice"],
    }),

    // POST a new vehicle
    addMaintainance: builder.mutation({
      query: (data) => ({
        url: "",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["maintainanceSlice"],
    }),

    // PUT update vehicle by ID
    updateMaintainance: builder.mutation({
      query: ({ data, id }) => ({
        url: `${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["maintainanceSlice"],
    }),

    // DELETE a vehicle
    deleteMaintainance: builder.mutation({
      query: (id) => ({
        url: `${id}`,
        method: "Delete",
      }),
      invalidatesTags: ["maintainanceSlice"],
    }),
  }),
});
// Export hooks for usage in components
export const {
  useGetAllMaintainanceQuery,
  useAddMaintainanceMutation,
  useUpdateMaintainanceMutation,
  useDeleteMaintainanceMutation,
  useGetMaintainanceDetailsQuery,
} = maintainanceSlice;
